import { registerPropDrawers } from './propRegistry';
import { OUTLINE, block, ellipse, rect, shadow, type Ctx } from './paint';
import type { Rect } from '../world/mapdef';

/** Props of the life package: the piggy bank, the Burger Corner counter and the mall ad screen. */
const FONT = '"PingFang TC", "Noto Sans TC", system-ui, sans-serif';

function text(c: Ctx, s: string, x: number, y: number, size: number, col: string): void {
  c.font = `900 ${size}px ${FONT}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = col;
  c.fillText(s, x, y);
}

function piggy(c: Ctx, r: Rect, t: number): void {
  const cx = r.x + r.w / 2, by = r.y + r.h;
  shadow(c, cx, by, 8, 2.2);
  // Little wooden stool.
  block(c, cx - 7, by - 9, 14, 3, '#a0683c', '#c08a58', '#7a4a26');
  rect(c, '#6b3f22', cx - 6, by - 6, 1.6, 6);
  rect(c, '#6b3f22', cx + 4.4, by - 6, 1.6, 6);
  // The pig: round pink body, ears, snout, legs and a coin slot.
  const top = by - 9;
  ellipse(c, '#b0506e', cx, top - 5, 7.4, 5.6);
  ellipse(c, '#ff9fbf', cx, top - 5.2, 6.6, 4.9);
  ellipse(c, '#ffc4d6', cx - 1.5, top - 7, 3.5, 2);
  rect(c, '#e47a9c', cx - 5, top - 1.5, 2, 1.6);
  rect(c, '#e47a9c', cx + 3, top - 1.5, 2, 1.6);
  c.fillStyle = '#e47a9c';
  c.beginPath();
  c.moveTo(cx - 4.5, top - 9);
  c.lineTo(cx - 2.6, top - 11.5);
  c.lineTo(cx - 1.4, top - 9);
  c.fill();
  ellipse(c, '#ff86ac', cx + 5.6, top - 4.6, 2, 1.7);
  rect(c, '#b0506e', cx + 5, top - 5, 0.7, 0.8);
  rect(c, '#b0506e', cx + 6.2, top - 5, 0.7, 0.8);
  rect(c, OUTLINE, cx + 2.2, top - 7, 1, 1);
  rect(c, '#7a2c48', cx - 2, top - 10, 4, 0.9);
  // Every few seconds a coin drops into the slot.
  const k = (t % 4) / 4;
  if (k < 0.25) {
    const y = top - 18 + k * 4 * 8;
    ellipse(c, '#9a6a10', cx, y, 2.2, 2.2);
    ellipse(c, '#ffd84a', cx, y, 1.7, 1.7);
  } else if (k < 0.32) {
    c.fillStyle = `rgba(255,240,170,${1 - (k - 0.25) / 0.07})`;
    for (const dx of [-3, 0, 3]) c.fillRect(cx + dx, top - 13 - Math.abs(dx) * 0.3, 1, 1);
  }
}

function fastFood(c: Ctx, r: Rect, t: number): void {
  const { x, y, w, h } = r;
  // Back wall panel with a glowing menu board.
  block(c, x - 2, y - 44, w + 4, 30, '#c0392b', '#e05a48', '#8e2a20');
  rect(c, '#ffd84a', x - 2, y - 44, w + 4, 2);
  text(c, 'BURGER CORNER', x + w / 2, y - 39.5, 5.5, '#fff3c4');
  const glow = 0.85 + Math.sin(t * 3) * 0.08;
  const icons = ['#e8a050', '#ffd34e', '#57c278'];
  for (let i = 0; i < 3; i++) {
    const px = x + 4 + i * ((w - 8) / 3);
    const pw = (w - 8) / 3 - 2;
    block(c, px, y - 35, pw, 12, '#2a2230');
    rect(c, `rgba(255,248,220,${glow})`, px + 1, y - 34, pw - 2, 10);
    ellipse(c, icons[i], px + 5, y - 29, 3, 2.4);
    rect(c, '#5a4a3a', px + 10, y - 31, pw - 13, 1);
    rect(c, '#c0392b', px + 10, y - 28, pw - 16, 1.2);
  }
  // Cashier behind the counter: red cap, smile, red shirt.
  const kx = x + w * 0.62;
  const bob = Math.sin(t * 2) * 0.4;
  block(c, kx - 5, y - 18 + bob, 10, 8, '#d0453a', '#ec6a5c');
  ellipse(c, '#f3c9a0', kx, y - 21 + bob, 3.6, 3.4);
  rect(c, '#d0453a', kx - 4, y - 25 + bob, 8, 2.2);
  rect(c, '#ffd84a', kx - 1, y - 25 + bob, 2, 1);
  rect(c, OUTLINE, kx - 1.6, y - 21.5 + bob, 0.9, 0.9);
  rect(c, OUTLINE, kx + 0.8, y - 21.5 + bob, 0.9, 0.9);
  rect(c, '#b05040', kx - 0.8, y - 19.6 + bob, 1.6, 0.6);
  // The counter: red and white with a yellow stripe.
  shadow(c, x + w / 2, y + h, w / 2 + 1, 3);
  block(c, x, y - 10, w, h + 10, '#f4efe6', '#ffffff', '#cfc6b8');
  rect(c, '#d0453a', x + 1, y - 3, w - 2, 4);
  rect(c, '#ffd84a', x + 1, y + 1, w - 2, 1.2);
  rect(c, 'rgba(0,0,0,0.15)', x + 1, y - 1, w - 2, 0.8);
  // Register, a tray with a burger, fries and a cup.
  block(c, x + w - 16, y - 17, 11, 7, '#3c4652', '#56616e');
  rect(c, '#7ce0a0', x + w - 14, y - 15.5, 6, 2);
  block(c, x + 5, y - 13, 22, 3, '#b8814e', '#d9a876');
  ellipse(c, '#c9853c', x + 10, y - 15, 3.4, 1.6);
  rect(c, '#57c278', x + 7, y - 14.6, 6, 0.8);
  ellipse(c, '#e8a050', x + 10, y - 13.8, 3.4, 1);
  block(c, x + 16, y - 18, 4, 5, '#d0453a');
  for (let i = 0; i < 3; i++) rect(c, '#ffd34e', x + 16.3 + i * 1.2, y - 20 - (i % 2), 0.8, 3);
  block(c, x + 22, y - 18, 4, 5, '#ffffff', undefined, '#cfc6b8');
  rect(c, '#d0453a', x + 23.5, y - 20, 0.6, 3);
  // Steam from the fries.
  for (let i = 0; i < 2; i++) {
    const k = (t * 0.7 + i * 0.5) % 1;
    c.fillStyle = `rgba(255,255,255,${0.4 * (1 - k)})`;
    c.fillRect(x + 17 + Math.sin(k * 6 + i) * 1.5, y - 21 - k * 7, 1, 1.4);
  }
}

function adScreen(c: Ctx, r: Rect, t: number): void {
  const { x, y, w, h } = r;
  const cx = x + w / 2;
  shadow(c, cx, y + h, w / 2, 2.5);
  // Stand.
  block(c, cx - 2, y - 10, 4, h + 10, '#3a3f4a', '#56616e');
  block(c, x + 4, y + h - 3, w - 8, 3, '#2b2f38');
  // Screen with a pulsing frame.
  const top = y - 40;
  const hue = (t * 90) % 360;
  block(c, x - 1, top, w + 2, 30, '#1b1d24', '#2b2f38');
  c.fillStyle = `hsl(${hue},90%,60%)`;
  c.fillRect(x, top + 1, w, 1);
  c.fillRect(x, top + 28, w, 1);
  const flash = Math.floor(t * 2.5) % 2 === 0;
  c.fillStyle = flash ? '#ff3d7f' : '#ffd23a';
  c.fillRect(x + 1.5, top + 3, w - 3, 24);
  // Starburst and "SALE" text.
  c.save();
  c.translate(x + w - 6, top + 7);
  c.rotate(t * 1.5);
  c.fillStyle = flash ? '#ffd23a' : '#ff3d7f';
  c.beginPath();
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2, rr = i % 2 ? 3 : 6;
    c.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
  }
  c.fill();
  c.restore();
  text(c, '%', x + w - 6, top + 7.5, 5, flash ? '#c0164f' : '#7a3a00');
  text(c, 'SALE!', cx - 4, top + 12, 8, '#ffffff');
  text(c, 'LIMITED', cx, top + 21, 5.5, flash ? '#3a0a1a' : '#5a2a00');
  // Sparkles that blink around the frame.
  for (let i = 0; i < 6; i++) {
    const k = (t * 1.3 + i / 6) % 1;
    if (k > 0.5) continue;
    c.fillStyle = `rgba(255,255,255,${1 - k * 2})`;
    const sx = x + ((i * 37) % w), sy = top + ((i * 13) % 26) + 2;
    c.fillRect(sx, sy, 1, 1);
  }
}

registerPropDrawers({
  objects: { life_piggy: piggy, life_fastfood: fastFood, life_adscreen: adScreen },
  animatedObjects: ['life_piggy', 'life_fastfood', 'life_adscreen'],
});
